import { ArrowDown, ArrowUp, ArrowsLeftRight, ArrowLineUp } from '@phosphor-icons/react';
import { getAsset, NETWORKS, type AssetCode } from '@onyx/shared';
import { useActivity, useWallet } from '../lib/queries';
import { useFiat, usePortfolio } from '../lib/app';
import { useLang, useT } from '../lib/i18n';
import { cryptoParts, displayDigits, formatCrypto, formatFiat } from '../lib/format';
import { navigate } from '../lib/router';
import { standardTag } from '../lib/networks';
import { AssetChip } from '../components/AssetChip';
import { NetworkMark } from '../components/NetworkMark';
import { Money, Row, Screen, ScreenHeader, Section, Skeleton } from '../components/ui';
import { ActivityRow } from './History';

export function AssetScreen({ code }: { code: AssetCode }) {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const wallet = useWallet();
  const def = getAsset(code)!;
  const { list, total } = usePortfolio(wallet.data, fiat);
  const h = list.find((x) => x.asset === code);
  const activity = useActivity({ asset: code }, 20);
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];
  const has = (h?.amount ?? 0) > 0;

  return (
    <Screen className="asset-screen">
      <ScreenHeader title={def.name} />
      <div className="card asset-hero">
        <AssetChip asset={code} size={56} />
        {wallet.isPending ? (
          <Skeleton w={200} h={48} r={12} />
        ) : (
          <Money parts={cryptoParts(String(h?.amount ?? 0), displayDigits(h?.amount ?? 0, def.stable), lang)} unit={code} size="display" />
        )}
        <p className="asset-hero__sub num">
          {formatFiat(h?.value ?? 0, fiat, lang)}
          {total > 0 && has && ` · ${Math.round(((h?.value ?? 0) / total) * 100)}%`}
        </p>
        {h && Number(h.locked) > 0 && (
          <p className="asset-hero__locked num">
            {t['asset.available']}: {formatCrypto(h.available, def.decimals, lang)} {code} ·{' '}
            <span className="is-warn">
              {t['asset.locked']}: {formatCrypto(h.locked, def.decimals, lang)} {code}
            </span>
          </p>
        )}
      </div>

      <nav className="asset-actions" aria-label={def.name}>
        {[
          { label: t['home.deposit'], Icon: ArrowDown, to: `/deposit/${code}`, off: false },
          { label: t['home.send'], Icon: ArrowUp, to: `/send/${code}`, off: !has },
          { label: t['home.swap'], Icon: ArrowsLeftRight, to: `/swap?from=${code}`, off: !has },
          { label: t['home.withdraw'], Icon: ArrowLineUp, to: `/withdraw/${code}`, off: !has },
        ].map(({ label, Icon, to, off }) => (
          <button key={label} type="button" className="asset-actions__item" disabled={off} onClick={() => navigate(to)}>
            <span className="asset-actions__icon">
              <Icon size={22} weight="bold" aria-hidden="true" />
            </span>
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <Section title={`${t['asset.networks']} · ${def.networks.length}`}>
        <div className="rows">
          {def.networks.map((n) => (
            <Row
              key={n.network}
              onClick={() => navigate(`/deposit/${code}/${n.network}`)}
              leading={<NetworkMark network={n.network} size={36} />}
              title={
                <>
                  {NETWORKS[n.network].name}
                </>
              }
              subtitle={[standardTag(n.standard), t['pick.minutes'](NETWORKS[n.network].minutes)].filter(Boolean).join(' · ')}
              trailing={
                <span className="num">
                  {formatCrypto(n.withdrawFee, 8, lang)} {code}
                </span>
              }
              trailingSub={t['pick.fee']}
              chevron
            />
          ))}
        </div>
      </Section>

      <Section title={t['asset.history']}>
        {activity.isPending ? (
          <Skeleton w="100%" h={56} r={12} />
        ) : items.length ? (
          <div className="rows">
            {items.map((a) => (
              <ActivityRow key={a.id} item={a} />
            ))}
          </div>
        ) : (
          <p className="state">{t['asset.noHistory']}</p>
        )}
      </Section>
    </Screen>
  );
}
